import {formatGroszeAsZlotyLatex} from "../../../../utils/formatGroszeAsZlotyLatex";
﻿// src/generators/dzial2_algebra/temat5_zadania_uzupelniajace_maturalne/
// podtemat3_maturalne_otwarte_types/MaturaTextLinearSystemOpenTask.js

import React from "react";
import { InlineMath, BlockMath } from "react-katex";

import FractionInput, {
  formatLatexPreview as formatFracPreview,
} from "../../../../components/inputs/FractionInput";

import {
  parseRootFractionInput,
  formatLatexPreview as formatNumLatex,
} from "../../../../components/inputs/RootFractionInput";

import EquationInput from "../../../../components/inputs/EquationInput";
import OpenTaskScoringHint from "../../../../components/ui/OpenTaskScoringHint";

/* ============================================================================
 * HELPERY
 * ========================================================================== */

const tol = 1e-6;
const eq = (a, b) => Math.abs(a - b) <= tol;

const randInt = (min, max) =>
  Math.floor(Math.random() * (max - min + 1)) + min;

function normalizeUserNumberText(txt) {
  return String(txt ?? "")
    .replace(/[−–—]/g, "-")
    .replace(/\u00A0|\u202F|\u2009|\u200A|\u200B/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function parseNumberFromAny(v) {
  if (v == null) return NaN;

  if (typeof v === "object") {
    if (Number.isFinite(v.parsed)) return v.parsed;
    if (typeof v.raw === "string") {
      const s = normalizeUserNumberText(v.raw).replace(",", ".");
      return parseRootFractionInput(s);
    }
  }

  if (typeof v === "number") return v;

  if (typeof v === "string") {
    const s = normalizeUserNumberText(v).replace(",", ".");
    return parseRootFractionInput(s);
  }

  return NaN;
}

function getFractionRaw(v) {
  if (!v) return "";
  if (typeof v === "string") return v;
  if (typeof v === "object" && typeof v.raw === "string") return v.raw ?? "";
  return "";
}

// grosze -> tekst "752,52"
function moneyTextPL(grosze) {
  const sign = grosze < 0 ? "-" : "";
  const abs = Math.abs(grosze);
  const zl = Math.floor(abs / 100);
  const gr = abs % 100;
  const grStr = gr < 10 ? `0${gr}` : String(gr);
  return `${sign}${zl},${grStr}`;
}

// grosze -> latex "752.52"
function moneyLatex(grosze) {
  return formatGroszeAsZlotyLatex(grosze);
}

/** Podgląd LaTeX dla równania wpisanego w EquationInput */
function toLatexEquationPreview(str) {
  let s = String(str ?? "");

  // mieszane ułamki: "3 1/2" -> "3\\dfrac{1}{2}"
  s = s.replace(
    /(-?\d+)\s+(\d+)\/(\d+)/g,
    (_, w, n, d) => `${w}\\dfrac{${n}}{${d}}`
  );

  // zwykłe ułamki: "-3/4" -> "\\dfrac{-3}{4}" (ale nie ruszaj już \\dfrac{...})
  s = s.replace(
    /(?<!\\dfrac\{)(-?\d+)\/(\d+)/g,
    (_, n, d) => `\\dfrac{${n}}{${d}}`
  );

  // wielocyfrowe wykładniki: 2^36 -> 2^{36}, 2^-12 -> 2^{-12}
  s = s.replace(/\^(-?\d{2,})/g, "^{$1}");

  // mnożenie: gwiazdka lub środkowa kropka -> \\cdot
  s = s.replace(/\*/g, "\\cdot ");
  s = s.replace(/·/g, "\\cdot ");

  return s;
}

/* ============================================================================
 * PARSOWANIE RÓWNAŃ LINIOWYCH
 * ========================================================================== */

function parseLinearExpr(expr) {
  if (expr == null) return null;
  let s = String(expr).trim();
  if (!s) return { ax: 0, ay: 0, c: 0 };

  s = s.replace(/,/g, "."); // przecinki jako kropki
  s = s.replace(/\s+/g, "");
  s = s.replace(/·/g, "*");

  // zamiana na sumę: "-" -> "+-"
  s = s.replace(/-/g, "+-");
  if (s[0] === "+") s = s.slice(1);

  const parts = s.split("+").filter((t) => t !== "");
  if (!parts.length) return null;

  let ax = 0;
  let ay = 0;
  let c = 0;

  const parseProduct = (value) => {
    if (value === "" || value === "+") return 1;
    if (value === "-") return -1;

    return value.split("*").reduce((product, factor) => {
      if (!Number.isFinite(product)) return NaN;
      const fraction = factor.split("/");
      if (fraction.length > 2) return NaN;
      const numerator = Number(fraction[0]);
      const denominator = fraction.length === 2 ? Number(fraction[1]) : 1;
      if (!Number.isFinite(numerator) || !Number.isFinite(denominator) || denominator === 0) {
        return NaN;
      }
      return product * numerator / denominator;
    }, 1);
  };

  for (const term of parts) {
    let t = term;
    if (t.includes("x") && t.includes("y")) {
      return null; // nie obsługujemy xy itd.
    }
    if (t.includes("x")) {
      const coefStr = t.replace("x", "");
      const coef = parseProduct(coefStr);
      if (!Number.isFinite(coef)) return null;
      ax += coef;
      continue;
    }
    if (t.includes("y")) {
      const coefStr = t.replace("y", "");
      const coef = parseProduct(coefStr);
      if (!Number.isFinite(coef)) return null;
      ay += coef;
      continue;
    }
    const val = parseProduct(t);
    if (!Number.isFinite(val)) return null;
    c += val;
  }

  return { ax, ay, c };
}

/**
 * Zamienia równanie "lewa = prawa" na postać ax*x + ay*y + c = 0
 */
function parseEquationToStandard(str) {
  if (str == null) return null;
  let s = String(str).trim();
  if (!s) return null;

  s = s.replace(/,/g, ".");
  s = s.replace(/\s+/g, "");

  const parts = s.split("=");
  if (parts.length !== 2) return null;

  const left = parseLinearExpr(parts[0]);
  const right = parseLinearExpr(parts[1]);
  if (!left || !right) return null;

  return {
    ax: left.ax - right.ax,
    ay: left.ay - right.ay,
    c: left.c - right.c,
  };
}

function isBareSolvedEquation(value) {
  const normalized = String(value ?? "").replace(/\s+/g, "").replace(/,/g, ".");
  const numberPattern = "[+-]?(?:\\d+(?:\\.\\d+)?|\\d+\\/\\d+)";
  return new RegExp(`^(?:[xy]=${numberPattern}|${numberPattern}=[xy])$`).test(normalized);
}

/** Sprawdza równoważność dwóch równań (do stałej k != 0) */
function equationsEquivalent(e1, e2) {
  if (!e1 || !e2) return false;

  const { ax: a1, ay: b1, c: c1 } = e1;
  const { ax: a2, ay: b2, c: c2 } = e2;

  // jeżeli wszystkie współczynniki jednego są 0 -> odrzucamy
  if (eq(a1, 0) && eq(b1, 0) && eq(c1, 0)) return false;
  if (eq(a2, 0) && eq(b2, 0) && eq(c2, 0)) return false;

  // wybieramy bazowy współczynnik z równania wzorcowego
  let baseNum;
  let baseDen;
  if (!eq(a2, 0)) {
    baseNum = a1;
    baseDen = a2;
  } else if (!eq(b2, 0)) {
    baseNum = b1;
    baseDen = b2;
  } else {
    baseNum = c1;
    baseDen = c2;
  }

  if (eq(baseDen, 0)) return false;

  const k = baseNum / baseDen;
  if (!Number.isFinite(k) || eq(k, 0)) return false;

  return (
    eq(a1, k * a2) &&
    eq(b1, k * b2) &&
    eq(c1, k * c2)
  );
}

/* ============================================================================
 * GENERATOR FABUŁ
 * ========================================================================== */

function pickScenario() {
  const scenarios = [
    {
      id: "tomatoes",
      person: "Właściciel restauracji",
      totalLabel: "pomidorów",
      item1: "pomidorów malinowych",
      item2: "pomidorów cherry",
      unitShort: "kg",
      unitLong: "kilogram",
      allItemsAcc: "pomidory",
      question:
        "Oblicz, ile kilogramów pomidorów malinowych kupił właściciel restauracji.",
      answerUnit: "kg",
      answerNoun: "pomidory malinowe",
    },
    {
      id: "stationery",
      person: "Właściciel sklepu papierniczego",
      totalLabel: "artykułów",
      item1: "długopisów",
      item2: "zeszytów",
      unitShort: "sztuk",
      unitLong: "sztukę",
      allItemsAcc: "te zakupy",
      question:
        "Oblicz, ile sztuk długopisów kupił właściciel sklepu papierniczego.",
      answerUnit: "szt.",
      answerNoun: "długopisy",
    },
    {
      id: "fuel",
      person: "Właściciel firmy transportowej",
      totalLabel: "paliwa",
      item1: "benzyny",
      item2: "oleju napędowego",
      unitShort: "litrów",
      unitLong: "litr",
      allItemsAcc: "paliwo",
      question:
        "Oblicz, ile litrów benzyny kupił właściciel firmy transportowej.",
      answerUnit: "l",
      answerNoun: "benzyna",
    },
  ];

  const idx = randInt(0, scenarios.length - 1);
  return scenarios[idx];
}

/* ============================================================================
 * GENERATOR ZADANIA
 * ========================================================================== */

/**
 * Wariant „oryginał” – matura podstawowa, sierpień 2025, zadanie 11:
 *   75 kg pomidorów: x kg malinowych po 11 zł, y kg cherry po 7,98 zł,
 *   łącznie 752,52 zł.  →  x = 51, y = 24.
 */
function createOriginalTask() {
  const scenario = {
    id: "tomatoes",
    person: "Właściciel restauracji",
    totalLabel: "pomidorów",
    item1: "pomidorów malinowych",
    item2: "pomidorów cherry",
    unitShort: "kg",
    unitLong: "kilogram",
    allItemsAcc: "pomidory",
    question:
      "Oblicz, ile kilogramów pomidorów malinowych kupił właściciel restauracji.",
    answerUnit: "kg",
    answerNoun: "pomidory malinowe",
  };

  return {
    type: "MaturaTextLinearSystemOpenTask",
    data: {
      scenario,
      totalAmount: 75,
      price1: 1100, // 11,00 zł (w groszach)
      price2: 798, // 7,98 zł
      totalCost: 75252, // 752,52 zł
      xCorrect: 51,
      yCorrect: 24,
      maxPoints: 2,
    },
    source: {
      variant: "original",
      exam: "MATURA",
      session: "2025_SIE",
      taskNumber: 11,
    },
  };
}

export function generateTask(generationOptions = {}) {
  if (generationOptions.variant === "original") return createOriginalTask();

  const scenario = pickScenario();

  const totalAmount = randInt(40, 120);

  const xCorrect = randInt(10, totalAmount - 10);
  const yCorrect = totalAmount - xCorrect;

  const price1 = randInt(700, 1800);
  let price2 = randInt(500, 1600);
  while (price2 === price1) {
    price2 = randInt(500, 1600);
  }

  const totalCost = xCorrect * price1 + yCorrect * price2;

  return {
    type: "MaturaTextLinearSystemOpenTask",
    data: {
      scenario,
      totalAmount,
      price1,
      price2,
      totalCost,
      xCorrect,
      yCorrect,
      maxPoints: 2,
    },
    source: { variant: "random", basedOn: "MATURA_2025_SIE_11" },
  };
}

/* ============================================================================
 * TREŚĆ ZADANIA
 * ========================================================================== */

export function renderTask({ data }) {
  const { scenario, totalAmount, price1, price2, totalCost } = data;

  const totalLatex = String(totalAmount);
  const unitShortLatex = `\\text{${scenario.unitShort}}`;

  const p1Latex = moneyLatex(price1);
  const p2Latex = moneyLatex(price2);
  const totalCostLatex = moneyLatex(totalCost);

  const p1Text = moneyTextPL(price1);
  const p2Text = moneyTextPL(price2);
  const totalCostText = moneyTextPL(totalCost);

  return (
    <div style={{ lineHeight: 1.5 }}>
      <p>
        {scenario.person} kupił{" "}
        <InlineMath math={`${totalLatex}\\,${unitShortLatex}`} />{" "}
        {scenario.totalLabel}: <InlineMath math={"x"} />{" "}
        <InlineMath math={unitShortLatex} /> {scenario.item1} w cenie{" "}
        <InlineMath math={`${p1Latex}\\text{\\,zł}`} /> za{" "}
        {scenario.unitLong} oraz <InlineMath math={"y"} />{" "}
        <InlineMath math={unitShortLatex} /> {scenario.item2} w cenie{" "}
        <InlineMath math={`${p2Latex}\\text{\\,zł}`} /> za {scenario.unitLong}.
        Za {scenario.allItemsAcc} zapłacił łącznie{" "}
        <InlineMath math={`${totalCostLatex}\\text{\\,zł}`} />.
      </p>
      <p style={{ marginTop: "0.5rem" }}>
        Zapisz układ równań opisujący sytuację i rozwiąż go.
      </p>
    </div>
  );
}

/* ============================================================================
 * INPUT UŻYTKOWNIKA
 * ========================================================================== */

export function renderInput({ data }, userInput = {}, onChange, disabled) {
  const { eq1 = "", eq2 = "", x = "" } = userInput;

  return (
    <div className="flex flex-col gap-4">
      <OpenTaskScoringHint variant="methodOptional" />

      {/* Krok 1 – układ równań */}
      <div className="flex flex-col gap-2">
        <strong>Krok 1.</strong>
        <div>Równanie lub układ równań opisujący sytuację z zadania:</div>

        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-1">
            <div>Pierwsze równanie:</div>
            <EquationInput
              value={eq1}
              onChange={(val) => onChange({ ...userInput, eq1: val })}
              disabled={disabled}
              placeholder="np. x + y = 60"
            />
          </div>

          <div className="flex flex-col gap-1">
            <div>Drugie równanie (opcjonalne przy metodzie z jednym równaniem):</div>
            <EquationInput
              value={eq2}
              onChange={(val) => onChange({ ...userInput, eq2: val })}
              disabled={disabled}
              placeholder="np. 7.75x + 12.49y = 662.73"
            />
          </div>
        </div>
      </div>

      {/* Krok 2 – rozwiązanie układu */}
      <div className="flex flex-col gap-2">
        <strong>Krok 2.</strong>
        <div>
          Rozwiąż zadanie i oblicz wartość <InlineMath math="x" />, o którą pyta polecenie.
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <InlineMath math={"x\\;="} />
          <FractionInput
            value={x}
            onChange={(val) => onChange({ ...userInput, x: val })}
            disabled={disabled}
            label="x"
            placeholder="np. 51"
          />

        </div>
      </div>
    </div>
  );
}

/* ============================================================================
 * PODGLĄD ODPOWIEDZI UŻYTKOWNIKA
 * ========================================================================== */

export function renderUserAnswer(userInput = {}) {
  const eq1 = String(userInput.eq1 ?? "").trim();
  const eq2 = String(userInput.eq2 ?? "").trim();

  const xRaw = getFractionRaw(userInput.x);
  const yRaw = getFractionRaw(userInput.y);

  const xLatex =
    xRaw &&
    (formatFracPreview(xRaw, "") ||
      formatNumLatex(String(parseNumberFromAny(userInput.x)), ""));
  const yLatex =
    yRaw &&
    (formatFracPreview(yRaw, "") ||
      formatNumLatex(String(parseNumberFromAny(userInput.y)), ""));

  const anyEq = eq1 || eq2;
  const anyXY = xLatex || yLatex;

  if (!anyEq && !anyXY) return <i>(brak odpowiedzi)</i>;

  return (
    <div className="space-y-1" style={{ textAlign: "left" }}>
      {anyEq && (
        <div>
          <BlockMath
            math={String.raw`\begin{cases}
${toLatexEquationPreview(eq1 || "")} \\
${toLatexEquationPreview(eq2 || "")}
\end{cases}`}
          />
        </div>
      )}
      {xLatex && (
        <div>
          <InlineMath math={`x = ${xLatex}`} />
        </div>
      )}
      {yLatex && (
        <div>
          <InlineMath math={`y = ${yLatex}`} />
        </div>
      )}
    </div>
  );
}

/* ============================================================================
 * KRÓTKA POPRAWNA ODPOWIEDŹ
 * ========================================================================== */

export function renderAnswer({ data }) {
  const { totalAmount, price1, price2, totalCost, xCorrect, yCorrect } = data;

  const p1Latex = moneyLatex(price1);
  const p2Latex = moneyLatex(price2);
  const totalLatex = moneyLatex(totalCost);

  const xL = String(xCorrect);
  const yL = String(yCorrect);

  const systemLatex = String.raw`\begin{cases}
x + y = ${totalAmount} \\
${p1Latex}x + ${p2Latex}y = ${totalLatex}
\end{cases}`;

  const solLatex = String.raw`x = ${xL},\quad y = ${yL}`;

  return (
    <div
      style={{
        textAlign: "left",
        display: "flex",
        flexDirection: "column",
        gap: "0.4rem",
        lineHeight: 1.4,
      }}
    >
      <div>
        <BlockMath math={systemLatex} />
      </div>
      <div>
        <InlineMath math={solLatex} />
      </div>
    </div>
  );
}

/* ============================================================================
 * SPRAWDZANIE (0–2 pkt)
 * ========================================================================== */

export function checkAnswer({ data }, userInput = {}) {
  const { totalAmount, price1, price2, totalCost, xCorrect, maxPoints } =
    data;

  const eq1Str = userInput.eq1;
  const eq2Str = userInput.eq2;

  const E1 = parseEquationToStandard(eq1Str);
  const E2 = parseEquationToStandard(eq2Str);

  // oczekiwane równania w postaci ax*x + ay*y + c = 0
  const expected1 = { ax: 1, ay: 1, c: -totalAmount };

  const p1 = price1 / 100;
  const p2 = price2 / 100;
  const S = totalCost / 100;

  const expected2 = { ax: p1, ay: p2, c: -S };
  const singleInX = {
    ax: p1 - p2,
    ay: 0,
    c: p2 * totalAmount - S,
  };
  const singleInY = {
    ax: 0,
    ay: p2 - p1,
    c: p1 * totalAmount - S,
  };

  let systemOK = false;
  if (E1 && E2) {
    const option1 =
      equationsEquivalent(E1, expected1) && equationsEquivalent(E2, expected2);
    const option2 =
      equationsEquivalent(E1, expected2) && equationsEquivalent(E2, expected1);
    systemOK = option1 || option2;
  } else {
    const single = E1 || E2;
    const singleRaw = E1 ? eq1Str : eq2Str;
    systemOK = Boolean(
      single &&
      !isBareSolvedEquation(singleRaw) &&
      (equationsEquivalent(single, singleInX) || equationsEquivalent(single, singleInY))
    );
  }

  const ux = parseNumberFromAny(userInput.x);
  const solutionOK = Number.isFinite(ux) && eq(ux, xCorrect);

  // Poprawne rozwiązanie końcowe ma pierwszeństwo nad błędem formalnym
  // w zapisie układu; niepoprawny układ nadal pozostaje widoczny w częściach.
  const score = solutionOK ? maxPoints : (systemOK ? 1 : 0);

  const parts = {
    "równanie / układ równań opisujący zadanie": systemOK,
    "odpowiedź końcowa (x)": solutionOK,
  };

  return {
    isCorrect: score === maxPoints,
    score,
    maxScore: maxPoints,
    parts,
  };
}

/* ============================================================================
 * WYJAŚNIENIE KROK PO KROKU
 * ========================================================================== */

export function renderExplanation({ data }) {
  const { scenario, totalAmount, price1, price2, totalCost, xCorrect, yCorrect } =
    data;

  const p1Text = moneyTextPL(price1);
  const p2Text = moneyTextPL(price2);
  const totalCostText = moneyTextPL(totalCost);

  const p1Latex = moneyLatex(price1);
  const p2Latex = moneyLatex(price2);
  const totalLatex = moneyLatex(totalCost);

  const xL = String(xCorrect);
  const yL = String(yCorrect);

  const systemLatex = String.raw`\begin{cases}
x + y = ${totalAmount} \\
${p1Latex}x + ${p2Latex}y = ${totalLatex}
\end{cases}`;

  const solLatex = String.raw`x = ${xL},\quad y = ${yL}`;

  return (
    <div className="space-y-3" style={{ textAlign: "left" }}>
      <p>
        <strong>Krok 1.</strong> Oznaczamy zmienne:
      </p>
      <ul style={{ paddingLeft: "1.2rem", marginTop: 0 }}>
        <li>
          <InlineMath math={"x"} /> – liczba {scenario.item1},
        </li>
        <li>
          <InlineMath math={"y"} /> – liczba {scenario.item2}.
        </li>
      </ul>

      <p>Na podstawie treści zadania zapisujemy układ równań:</p>
      <BlockMath math={systemLatex} />

      <p>
        Pierwsze równanie opisuje łączną liczbę {scenario.totalLabel}, a drugie
        – łączny koszt zakupów (cena razy liczba jednostek każdego rodzaju).
        Można zapisać równoważne równania, np. po przemnożeniu przez <InlineMath math="100" /> lub
        przeniesieniu wyrazów na jedną stronę.
      </p>

      <p>
        <strong>Krok 2.</strong> Rozwiązujemy układ równań (np. metodą
        podstawiania lub dodawania). Otrzymujemy:
      </p>
      <BlockMath math={solLatex} />

      <p>
        <strong>Krok 3.</strong> Interpretujemy wynik w kontekście zadania.
      </p>
      <p>
        {scenario.person} kupił <InlineMath math={xL} />{" "}
        <InlineMath math={`\\text{${scenario.answerUnit}}`} /> {scenario.answerNoun} oraz{" "}
        <InlineMath math={yL} /> <InlineMath math={`\\text{${scenario.unitShort}}`} /> {scenario.item2}.
      </p>

      <p style={{ marginTop: "0.5rem" }}>
        Sprawdzenie: łączna liczba jednostek to{" "}
        <InlineMath math={`${xL} + ${yL} = ${totalAmount}`} />, a łączny koszt:
      </p>
      <BlockMath
        math={String.raw`
${p1Latex}\cdot ${xL} + ${p2Latex}\cdot ${yL} = ${totalLatex}
`}
      />
      <p>
        czyli <InlineMath math={`${totalLatex}\\text{ zł}`} />, tak jak w treści zadania – wynik jest
        poprawny.
      </p>
    </div>
  );
}
